import { lazy } from "react";
import AuthorizeUser from "../middleware/AuthorizeUser";
import LazyLoading from "../common/components/LazyLoading/LazyLoading";
import PropertyLayout from "../layout/PropertyLayOut";
import propertiesRoutes from "./properties/PropertiesRoutes";
import { Navigate, useRouteError } from "react-router-dom";
import DynamicServiceResolver from "./DynamicServiceResolver";

// Surfaces the real error (message + stack) instead of a blank placeholder, so a
// runtime throw anywhere under /properties is diagnosable at a glance.
function PropertiesErrorFallback() {
  const error = useRouteError();
  const msg = error?.message || String(error || "Unknown error");
  return (
    <div style={{ minHeight: "60vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div style={{ maxWidth: 560, width: "100%", border: "1px solid #f0c9c9", background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 18px 38px -24px rgba(16,40,80,0.3)" }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#153568" }}>Something went wrong on this page</h2>
        <p style={{ marginTop: 6, fontSize: 13.5, color: "#5b6b8a" }}>
          The page hit a runtime error. This is often a stale hot-reload — a refresh usually clears it.
        </p>
        <pre style={{ marginTop: 12, padding: 12, background: "#fbf1f1", color: "#a23b3b", borderRadius: 10, fontSize: 12, whiteSpace: "pre-wrap", overflowX: "auto" }}>{msg}</pre>
        <button
          onClick={() => window.location.reload()}
          style={{ marginTop: 14, height: 40, padding: "0 16px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#153568,#1c4b93)", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer" }}
        >
          Reload page
        </button>
      </div>
    </div>
  );
}
const HomePage = lazy(() => import("../pages/homepage/HomePage"));
const Calculators = lazy(() => import("../pages/calculators/Calculators"));
const IntelligenceShell = lazy(() => import("../pages/intelligence/v2/IntelligenceShell"));
const IntelExplorer = lazy(() => import("../pages/intelligence/v2/pages/Explorer"));
const IntelAtlas = lazy(() => import("../pages/intelligence/v2/pages/Atlas"));
const IntelAtlasVillage = lazy(() => import("../pages/intelligence/v2/pages/AtlasVillage"));
const IntelAtlasSro = lazy(() => import("../pages/intelligence/v2/pages/AtlasSro"));
const IntelPulse = lazy(() => import("../pages/intelligence/v2/pages/Pulse"));
const IntelLeaderboard = lazy(() => import("../pages/intelligence/v2/pages/Leaderboard"));
const IntelGenome = lazy(() => import("../pages/intelligence/v2/pages/Genome"));
const IntelParty = lazy(() => import("../pages/intelligence/v2/pages/Party"));
const IntelPropertyChain = lazy(() => import("../pages/intelligence/v2/pages/PropertyChain"));
const IntelWatchlist = lazy(() => import("../pages/intelligence/v2/pages/Watchlist"));
const IntelLease = lazy(() => import("../pages/intelligence/v2/pages/Lease"));
const IntelLeaseBuilding = lazy(() =>
  import("../pages/intelligence/v2/pages/LeaseBuilding"),
);
const IntelLeaseCompany = lazy(() =>
  import("../pages/intelligence/v2/pages/LeaseCompany"),
);
const IntelLeaseLocation = lazy(() =>
  import("../pages/intelligence/v2/pages/LeaseLocation"),
);
const IntelLeaseLeaderboard = lazy(() =>
  import("../pages/intelligence/v2/pages/LeaseLeaderboard"),
);
const IntelDownload = lazy(() => import("../pages/intelligence/v2/pages/Download"));
const State = lazy(() => import("../pages/StateDoccuments/State"));
const Dashboard = lazy(() => import("../pages/Dashboard/Dashboard"));
const Contact = lazy(() => import("../pages/Contact/Contact"));
const States = lazy(() => import("../pages/States/States"));
const AboutJaaga = lazy(() => import("../pages/about/AboutJaaga"));
const MyProperty = lazy(() => import("../pages/MyProperty/MyProperty"));
const MapsHub = lazy(() => import("../pages/maps/MapsHub"));
const SurveyGuidePage = lazy(() => import("../pages/landTools/SurveyGuidePage"));

// Public Survey Maps landing pages (copy in pages/landTools/landToolsContent.js).
// The two state paths are static, so they outrank the ":serviceSlug/*" catch-all.
const SURVEY_GUIDE_PATHS = [
  "/survey-number-map",
  "/telangana/survey-number-map",
  "/andhra-pradesh/survey-number-map",
  "/find-survey-number",
  "/draw-land-map",
];
const TermsConditions = lazy(
  () => import("../common/components/Footer/Policies/TermsConditions"),
);
const PrivacyPolicies = lazy(
  () => import("../common/components/Footer/Policies/PrivacyPolicies"),
);
const DeleteAccount = lazy(
  () => import("../common/components/Footer/Policies/DeleteAccount"),
);
const RefundCancellationPolicy = lazy(
  () => import("../common/components/Footer/Policies/RefundCancellationPolicy"),
);
const ShippingDeliveryPolicy = lazy(
  () => import("../common/components/Footer/Policies/ShippingDeliveryPolicy"),
);

const routes = [
  {
    path: "",
    element: (
      <LazyLoading>
        <HomePage />
      </LazyLoading>
    ),
  },
  {
    path: "intelligence",
    element: (
      <LazyLoading>
        <IntelligenceShell />
      </LazyLoading>
    ),
    children: [
      { index: true, element: <IntelExplorer /> },
      { path: "atlas", element: <IntelAtlas /> },
      { path: "atlas/v/:village", element: <IntelAtlasVillage /> },
      { path: "atlas/s/:sro", element: <IntelAtlasSro /> },
      { path: "pulse", element: <IntelPulse /> },
      { path: "leaderboard", element: <IntelLeaderboard /> },
      { path: "genome", element: <IntelGenome /> },
      { path: "genome/:documentId", element: <IntelGenome /> },
      { path: "party/:name", element: <IntelParty /> },
      { path: "property", element: <IntelPropertyChain /> },
      { path: "watchlist", element: <IntelWatchlist /> },
      { path: "lease", element: <IntelLease /> },
      { path: "lease/b/:building", element: <IntelLeaseBuilding /> },
      { path: "lease/c/:company", element: <IntelLeaseCompany /> },
      { path: "lease/v/:village", element: <IntelLeaseLocation /> },
      { path: "lease/leaderboard", element: <IntelLeaseLeaderboard /> },
    ],
  },
  {
    path: "intelligence/search",
    element: <Navigate to="/intelligence" replace />,
  },
  {
    // Mobile→web PDF bridge. Top-level (no IntelligenceShell wrapper,
    // splash screen has no nav chrome) and outside the `/app/*`
    // namespace claimed by Android App Links / iOS Universal Links.
    // Also added to the mobile→store-redirect exception list in
    // index.html so the redeem actually runs on a phone.
    path: "intel/download",
    element: (
      <LazyLoading>
        <IntelDownload />
      </LazyLoading>
    ),
  },
  {
    path: "intelligence/analytics",
    element: <Navigate to="/intelligence/atlas" replace />,
  },
  {
    path: "intelligence/public-search",
    element: <Navigate to="/intelligence" replace />,
  },
  {
    path: "properties",
    element: <PropertyLayout />,
    errorElement: <PropertiesErrorFallback />,
    children: propertiesRoutes,
  },

  {
    path: "terms",
    element: (
      <LazyLoading>
        <TermsConditions />
      </LazyLoading>
    ),
  },
  {
    path: "privacy-policy",
    element: (
      <LazyLoading>
        <PrivacyPolicies />
      </LazyLoading>
    ),
  },
  {
    // Public account-deletion request page (Google Play Data safety link).
    path: "delete-account",
    element: (
      <LazyLoading>
        <DeleteAccount />
      </LazyLoading>
    ),
  },
  {
    path: "refund-cancellation-policy",
    element: (
      <LazyLoading>
        <RefundCancellationPolicy />
      </LazyLoading>
    ),
  },
  {
    path: "shipping-delivery-policy",
    element: (
      <LazyLoading>
        <ShippingDeliveryPolicy />
      </LazyLoading>
    ),
  },
  {
    // Old misspelt URL, kept so existing links (payment gateway, search
    // results) still land on the policy.
    path: "shippping-delivery-policy",
    element: <Navigate to="/shipping-delivery-policy" replace />,
  },
  {
    path: "dashboard",
    element: (
      <AuthorizeUser>
        <LazyLoading>
          <Dashboard />
        </LazyLoading>
      </AuthorizeUser>
    ),
  },
  {
    path: "contactus",
    element: (
      <LazyLoading>
        <Contact />
      </LazyLoading>
    ),
  },
  // NOTE: no client route for /blogs — nginx reverse-proxies /blogs to the
  // blog app (studio-swart-xi.vercel.app). A react-router <Navigate> to an
  // external URL never worked here (it only rewrites the in-app path), so it
  // was removed to stop it shadowing the proxy.

  {
    path: "about",
    element: (
      <LazyLoading>
        <AboutJaaga />
      </LazyLoading>
    ),
  },

  {
    path: "/states",
    element: (
      <LazyLoading>
        <States />
      </LazyLoading>
    ),
  },

  {
    // EMI + stamp duty (moved out of the Survey Maps toolkit).
    path: "calculators",
    element: (
      <LazyLoading>
        <Calculators />
      </LazyLoading>
    ),
  },

  {
    path: "maps",
    element: (
      <LazyLoading>
        <MapsHub />
      </LazyLoading>
    ),
  },

  ...SURVEY_GUIDE_PATHS.map((p) => ({
    path: p.slice(1),
    element: (
      <LazyLoading>
        <SurveyGuidePage key={p} path={p} />
      </LazyLoading>
    ),
  })),

  {
    path: "rent",
    element: (
      <LazyLoading>
        <MyProperty />
      </LazyLoading>
    ),
  },

  {
    path: "state/:stateName",
    element: (
      <LazyLoading>
        <State />
      </LazyLoading>
    ),
  },

  {
    path: ":serviceSlug",
    element: (
      <LazyLoading>
        <DynamicServiceResolver />
      </LazyLoading>
    ),
  },
];

export default routes;
